import React from 'react';
import { DropzoneContainer } from '../../Styles/Styles';
import { useDropzone } from 'react-dropzone';

import { nanoid } from 'nanoid';

import { toBase64 } from '../../utils/Base64withPromise';

import { Typography, styled } from '@mui/material';

const PlusSign = styled(Typography)(({ disabled }) => ({
  fontSize: '20px',
  backgroundColor: disabled ? '#D3D3D3' : '#B7D423',
  borderRadius: '50%',
  height: '30px',
  width: '30px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  color: '#000',
  pointerEvents: disabled ? 'none' : 'auto',
  '&:hover': {
    backgroundColor: disabled ? '#D3D3D3' : '#45A049'
  }
}));

function CustomUpload({
  isClicked,
  dropzoneHeight,
  attachedfiles,
  acceptedFileTypes,
  maxFileSize,
  handleAttachmentSave,
  dropzoneEnabled,
  setDropzoneEnabled,
  errorMessage,
  setErrorMessage
}) {
  const onDrop = async acceptedFiles => {
    try {
      const maxTotalFiles = 5;
      const existingFileCount = attachedfiles?.length || 0;
      const remainingSlots = maxTotalFiles - existingFileCount;
      const nonPdfCount = acceptedFiles.reduce((count, file) => {
        if (!acceptedFileTypes.includes(file.type)) return count + 1;
        return count;
      }, 0);
      const accumulatedErrors = [];

      const uniqueAcceptedFiles = acceptedFiles.filter(
        file => !attachedfiles?.some(existing => existing.name === file.name)
      );
      const duplicateFiles = acceptedFiles.filter(file => attachedfiles?.some(existing => existing.name === file.name));

      if (duplicateFiles.length > 0) {
        accumulatedErrors.push(`Duplicate files found: ${duplicateFiles.map(file => file.name).join(', ')}`);
      }
      let filesToAdd = 0;
      if (remainingSlots > 0) {
        filesToAdd = Math.min(remainingSlots, uniqueAcceptedFiles.length);
        const newpayload = [];

        for (const file of acceptedFiles.slice(0, filesToAdd)) {
          const fileType = file.type || 'unknown';

          const fileAlreadyExists = attachedfiles?.some(existing => existing.name === file.name);

          if (fileAlreadyExists) {
            accumulatedErrors.push(`File '${file.name}' already exists.`);
            filesToAdd--;
            continue;
          }

          if (!acceptedFileTypes.some(type => file.type.startsWith(type))) {
            accumulatedErrors.push(`File type '${fileType}' is not allowed.`);
            filesToAdd--;
            continue;
          }

          if (file.size > maxFileSize) {
            accumulatedErrors.push('File size exceeds the maximum limit (5MB).');
            filesToAdd--;
            continue;
          }

          const id = nanoid(5);

          if (attachedfiles === null || Array.isArray(attachedfiles)) {
            const filebase64 = await toBase64(file);
            const payload = {
              id,
              name: file.name,
              content: file,
              dataUrl: filebase64,
              type: file.type,
              enableDelete: true
            };

            newpayload.push(payload);

            if (acceptedFiles.slice(0, remainingSlots)?.length - 1 === newpayload.slice(0, remainingSlots).length - 1) {
              handleAttachmentSave(newpayload);
            }
          }

          if (!fileAlreadyExists && acceptedFileTypes.includes(file.type) && file.size < maxFileSize) {
            setErrorMessage('');
          }
        }

        // Set the error message based on accumulated errors
        if (accumulatedErrors.length > 0) {
          setErrorMessage(accumulatedErrors.join(' '));
        } else {
          setErrorMessage('');
        }

        if (remainingSlots <= filesToAdd - nonPdfCount) {
          setDropzoneEnabled(false);
          setErrorMessage('Max files attached');
        }
      }
    } catch (error) {
      console.error('Error uploading file:', error);
    }
  };
  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    disabled: !dropzoneEnabled,
    accept: {
      'application/pdf': ['.pdf'],
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg']
    }
  });

  return (
    <div
      {...getRootProps()}
      style={{
        opacity: !dropzoneEnabled ? '0.5' : '1',
        height: '100%',
        cursor: dropzoneEnabled ? 'pointer' : 'not-allowed'
      }}
    >
      <input {...getInputProps()} />
      <DropzoneContainer
        className={isClicked ? 'blinking' : ''} //
        style={{
          height: dropzoneHeight,
          cursor: dropzoneEnabled ? 'pointer' : 'not-allowed'
        }}
      >
        <PlusSign disabled={!dropzoneEnabled}> + </PlusSign>
        {errorMessage && <p style={{ color: 'red' }}>{errorMessage}</p>}
        <div className='medium6' style={{ marginTop: '10px', marginBottom: '10px' }}>
          Drag and drop file here <br />
          to start workflow
        </div>
        <div
          className='medium5'
          style={{
            color: '#E96666'
          }}
        >
          Formats allowed : PDF, .jpeg, .png, .jpg
        </div>
      </DropzoneContainer>
    </div>
  );
}

export default CustomUpload;
